
import React from 'react';
import { ArrowRight, Shield, Lock, Info } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

const Privacy: React.FC = () => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  
  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className={cn("mb-6 md:mb-10", isMobile ? "text-center" : "text-center")}>
          <div className={cn(
            "inline-flex items-center justify-center rounded-full mb-4",
            isMobile ? "w-12 h-12" : "w-16 h-16 mb-5"
          )}>
            <Shield className={cn(
              "text-blue-600 dark:text-blue-400",
              isMobile ? "w-6 h-6" : "w-8 h-8"
            )} />
          </div>
          <h1 className={cn(
            "font-bold mb-3 text-gradient",
            isMobile ? "text-3xl" : "text-4xl md:text-5xl mb-4"
          )}>
            Privacy Policy
          </h1>
          <p className={cn(
            "text-muted-foreground max-w-2xl mx-auto",
            isMobile ? "text-base" : "text-lg"
          )}>
            At Chatrix, we're committed to protecting your privacy and ensuring your information is secure.
          </p>
        </div>
        
        {/* Main content */}
        <div className={cn(
          "rounded-xl mb-8",
          isMobile ? "p-5" : "rounded-2xl p-8 lg:p-10 mb-10",
          theme === 'light' 
            ? "bg-white/80 backdrop-blur-sm shadow-sm border border-slate-200/50" 
            : "bg-slate/5 backdrop-blur-sm border border-slate/10 shadow-md"
        )}>
          <div className={cn(
            "prose dark:prose-invert max-w-none",
            isMobile ? "prose-sm" : "prose-lg"
          )}>
            <section className={cn("mb-5", isMobile ? "" : "mb-6")}>
              <p className={cn(
                "italic mb-4",
                isMobile ? "text-xs text-left" : "text-sm text-center mb-6"
              )}>
                Last Updated: March 25, 2025
              </p>
              <p className={cn("mb-4", isMobile ? "text-left" : "")}>
                Welcome to <strong>Chatrix</strong>! This Privacy Policy governs your use of our website and services. 
                By accessing or using <strong>Chatrix</strong>, you agree to comply with this Privacy Policy. If you do not agree, please do not use the website.
              </p>
            </section>
            
            <section className={cn("mb-6", isMobile ? "" : "mb-10")}>
              <div className={cn("flex items-start gap-3", isMobile ? "mb-3" : "gap-4 mb-4")}>
                <div className={cn(
                  "rounded-lg flex-shrink-0",
                  isMobile ? "p-1.5 mt-0.5" : "p-2",
                  theme === 'light' ? "bg-blue-50" : "bg-blue-900/20"
                )}>
                  <Info className={cn(
                    isMobile ? "w-4 h-4" : "w-5 h-5", 
                    theme === 'light' ? "text-blue-600" : "text-blue-400"
                  )} />
                </div>
                <div>
                  <h2 className={cn(
                    "font-semibold",
                    isMobile ? "text-xl mb-3" : "text-2xl mb-4"
                  )}>
                    1. Data Collection & Usage
                  </h2>
                  <ul className={cn("space-y-1.5", isMobile ? "mb-3" : "space-y-2 mb-4")}>
                    <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                      <ArrowRight className={cn(
                        "flex-shrink-0 text-blue-500",
                        isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                      )} />
                      <span className="text-left">We <strong>collect minimal data</strong> (such as IP address, device type) for analytics and security purposes.</span>
                    </li>
                    <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                      <ArrowRight className={cn(
                        "flex-shrink-0 text-blue-500",
                        isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                      )} />
                      <span className="text-left"><strong>We do not sell, rent, or trade personal data</strong> with third parties.</span>
                    </li>
                    <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                      <ArrowRight className={cn(
                        "flex-shrink-0 text-blue-500",
                        isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                      )} />
                      <span className="text-left">User messages are <strong>not permanently stored</strong>; however, interactions may be logged temporarily for moderation and security.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>
            
            <section className={cn("mb-6", isMobile ? "" : "mb-10")}>
              <div className={cn("flex items-start gap-3", isMobile ? "mb-3" : "gap-4 mb-4")}>
                <div className={cn(
                  "rounded-lg flex-shrink-0",
                  isMobile ? "p-1.5 mt-0.5" : "p-2",
                  theme === 'light' ? "bg-blue-50" : "bg-blue-900/20"
                )}>
                  <Lock className={cn(
                    isMobile ? "w-4 h-4" : "w-5 h-5", 
                    theme === 'light' ? "text-blue-600" : "text-blue-400"
                  )} />
                </div>
                <div>
                  <h2 className={cn(
                    "font-semibold",
                    isMobile ? "text-xl mb-3" : "text-2xl mb-4"
                  )}>
                    2. Cookies & Tracking
                  </h2>
                  <ul className={cn("space-y-1.5", isMobile ? "mb-3" : "space-y-2 mb-4")}>
                    <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                      <ArrowRight className={cn(
                        "flex-shrink-0 text-blue-500",
                        isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                      )} />
                      <span className="text-left"><strong>Chatrix</strong> uses cookies to enhance user experience and analyze website traffic.</span>
                    </li>
                    <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                      <ArrowRight className={cn(
                        "flex-shrink-0 text-blue-500",
                        isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                      )} />
                      <span className="text-left">Third-party services (e.g., analytics, security tools) may collect anonymized usage data through cookies.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>
            
            <section className={cn("mb-6", isMobile ? "" : "mb-10")}>
              <h2 className={cn(
                "font-semibold",
                isMobile ? "text-xl mb-3" : "text-2xl mb-4"
              )}>
                3. Security Measures
              </h2>
              <ul className={cn(
                "space-y-1.5 grid gap-2",
                isMobile ? "gap-1.5 mb-3" : "gap-4 md:grid-cols-1 mb-4"
              )}>
                <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                  <ArrowRight className={cn(
                    "flex-shrink-0 text-blue-500",
                    isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                  )} />
                  <span className="text-left">We employ <strong>standard security practices</strong> to protect user data, but no online platform is completely secure.</span>
                </li>
                <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                  <ArrowRight className={cn(
                    "flex-shrink-0 text-blue-500",
                    isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                  )} />
                  <span className="text-left"><strong>Chatrix is not liable for data breaches, hacking incidents, or third-party vulnerabilities</strong>.</span>
                </li>
              </ul>
            </section>
            
            <section className={cn("mb-6", isMobile ? "" : "mb-10")}>
              <h2 className={cn(
                "font-semibold",
                isMobile ? "text-xl mb-3" : "text-2xl mb-4"
              )}>
                4. Third-Party Links
              </h2>
              <ul className={cn("space-y-1.5", isMobile ? "mb-3" : "space-y-2 mb-4")}>
                <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                  <ArrowRight className={cn(
                    "flex-shrink-0 text-blue-500",
                    isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                  )} />
                  <span className="text-left"><strong>Chatrix</strong> may contain links to third-party websites.</span>
                </li>
                <li className={cn("flex items-start gap-1.5", isMobile ? "" : "gap-2")}>
                  <ArrowRight className={cn(
                    "flex-shrink-0 text-blue-500",
                    isMobile ? "w-3 h-3 mt-1" : "w-4 h-4 mt-1"
                  )} />
                  <span className="text-left">We <strong>do not control or endorse</strong> third-party content and are <strong>not responsible</strong> for any issues arising from external sites.</span>
                </li>
              </ul>
            </section>
            
            <section>
              <h2 className={cn(
                "font-semibold",
                isMobile ? "text-xl mb-3" : "text-2xl mb-4"
              )}>
                5. Contact Information
              </h2>
              <p className={cn("mb-3", isMobile ? "text-left" : "mb-4")}>
                For any inquiries regarding this Privacy Policy, please contact us at <a href="mailto:contact@chatrix.fun" className="text-blue-600 dark:text-blue-400 underline underline-offset-2">contact@chatrix.fun</a>.
              </p>
              <p className={cn("mb-3", isMobile ? "text-left" : "mb-4")}>
                By using <strong>Chatrix</strong>, you acknowledge and agree to these Terms and our Privacy Policy. We <strong>may update these terms at any time</strong>, and it is your responsibility to review them periodically.
              </p>
              <p className={cn("mb-3", isMobile ? "text-left" : "mb-4")}>
                Enjoy using <strong>Chatrix</strong> responsibly and respectfully! 🚀
              </p>
            </section>
          </div>
        </div>
        
        {/* Updated date */}
        <div className="text-center text-sm text-muted-foreground">
          Last updated: March 25, 2025
        </div>
      </div>
    </div>
  );
};

export default Privacy;
